import { IDistanceSurcharge, updateDistanceSurcharge } from "@/api/settings";
import CloseIcon from "@mui/icons-material/Close";

import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Stack,
    Toolbar,
    Typography,
} from "@mui/material";
import { FormButton, FormContainer, FormTextFieldElement } from "@rhf-kit/mui";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { enqueueSnackbar } from "notistack";

interface Props {
    distanceSurcharge: IDistanceSurcharge | undefined;
    open: boolean;
    setOpen: (open: boolean) => void;
    setSelected: (distanceSurcharge: IDistanceSurcharge | undefined) => void;
    mutateDistanceSurcharges: () => void;
}

interface FormData {
    minimumMiles: number;
    surcharge: number;
}

export const UpdateDistanceSurchargeModal = ({
    distanceSurcharge,
    open,
    setOpen,
    mutateDistanceSurcharges,
    setSelected,
}: Props) => {
    const updateDistanceSurchargeMutation = useMutation({
        mutationFn: updateDistanceSurcharge,
        onSuccess: () => {
            mutateDistanceSurcharges();
            enqueueSnackbar("Distance surcharge successfully updated", {
                variant: "success",
            });
            setOpen(false);
            setSelected(undefined);
        },
        onError: (error: AxiosError<{ detail: string }>) => {
            if (
                error.response &&
                error.response.data &&
                error.response.data.detail
            ) {
                enqueueSnackbar(error.response.data.detail, {
                    variant: "error",
                });
            }
        },
    });

    if (!distanceSurcharge) {
        return null;
    }

    const defaultValues = {
        minimumMiles: distanceSurcharge.minimum_miles,
        surcharge: parseFloat((distanceSurcharge.surcharge / 100).toFixed(2)),
    };

    const handleSubmit = (data: FormData) => {
        updateDistanceSurchargeMutation.mutate({
            id: distanceSurcharge.id,
            minimum_miles: data.minimumMiles,
            surcharge: data.surcharge * 100,
        });
    };

    return (
        <Dialog
            fullWidth
            maxWidth="sm"
            open={open}
            onClose={() => setOpen(false)}
        >
            <DialogTitle>
                <Toolbar variant="dense">
                    <Typography variant="h6" fontWeight={600}>
                        Edit distance surcharge
                    </Typography>
                    <Box flexGrow={1} />
                    <IconButton edge="end" onClick={() => setOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </Toolbar>
            </DialogTitle>
            <DialogContent>
                <FormContainer
                    defaultValues={defaultValues}
                    onSuccess={handleSubmit}
                >
                    <Stack direction="column" spacing={2} my={2}>
                        <FormTextFieldElement
                            id="minimumMiles"
                            name="minimumMiles"
                            label="Minimum Miles"
                            type="number"
                            size="small"
                            required
                            rules={{
                                min: {
                                    value: 1,
                                    message: "Minimum Miles must be at least 1",
                                },
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment
                                        position="end"
                                        variant="standard"
                                    >
                                        miles
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <FormTextFieldElement
                            id="surcharge"
                            name="surcharge"
                            label="Surcharge"
                            type="number"
                            rules={{
                                min: {
                                    value: 1,
                                    message: "Surcharge must be at least $0.01",
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment
                                        position="start"
                                        variant="standard"
                                    >
                                        $
                                    </InputAdornment>
                                ),
                            }}
                            size="small"
                            required
                        />
                    </Stack>
                    <FormButton variant="contained" color="primary">
                        Edit Distance Surcharge
                    </FormButton>
                </FormContainer>
            </DialogContent>
        </Dialog>
    );
};
